// updated version with preview modal
import { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Layout from '../components/Layout';
import { FaUserEdit, FaFileMedical, FaHeartbeat, FaBell, FaNotesMedical, FaDownload, FaFilter, FaSort } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import api from '../utils/axios';

const columns = [
    {
      name: "Name",
      selector: row => `${row.firstName} ${row.lastName}`,
      sortable: true,
    },
    {
      name: "Gender",
      selector: row => row.gender,
      sortable: true,
    },
    {
      name: "Phone",
      selector: row => row.phone,
      sortable: true,
    },
    {
      name: "Email",
      selector: row => row.email || "unknown@example.com",
      sortable: true,
    },
    {
      name: "Created At",
      selector: row => new Date(row.createdAt || Date.now()).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Status",
      selector: row => row.disabled ? "Disabled" : "Active",
      sortable: true,
    },
    {
      name: "Actions",
      cell: row => (
        <div className="flex gap-2">
          <button
            className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
            onClick={() => handleDisableUser(row.id)}
          >
            Disable
          </button>
          <button
            className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
            onClick={() => handleDeleteUser(row.id)}
          >
            Delete
          </button>
        </div>
      ),
    }
  ];

export default function Patients() {
    const [filterText, setFilterText] = useState("");
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [activeTab, setActiveTab] = useState("vitals");
    const [previewFile, setPreviewFile] = useState(null);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 10,
        total: 0
    });
    const [filters, setFilters] = useState({
        gender: '',
        status: ''
    });
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: null
    });
    const [patientDetails, setPatientDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    useEffect(() => {
        fetchPatients();
    }, [pagination.page, filters, sortConfig]);

    const fetchPatients = async () => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = {
                page: parseInt(pagination.page, 10),
                limit: parseInt(pagination.perPage, 10),
                ...(sortConfig.key && { 
                    sortField: sortConfig.key,
                    sortDirection: sortConfig.direction 
                }),
                ...(filters.gender && { gender: filters.gender }),
                ...(filters.status && { status: filters.status })
            };

            const response = await api.get('/admin/users', { params: queryParams });
            // Ensure we have valid data before setting state
            if (response?.data) {
                setPatients(Array.isArray(response.data.users) ? response.data.users : []);
                setPagination(prev => ({
                    ...prev,
                    total: typeof response.data.total === 'number' ? response.data.total : 0
                }));
            } else {
                setPatients([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (error) {
            console.error('Error fetching patients:', error);
            setError(error.response?.data?.error || 'Failed to fetch patients');
            toast.error('Error fetching patients: ' + (error.response?.data?.error || error.message));
            setPatients([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDisableUser = async (userId) => {
        const confirmDisable = window.confirm(`Disable this user's account?`);
        if (!confirmDisable) return;

        try {
            await api.patch(`/admin/users/${userId}/disable`);
            toast.success('User disabled successfully');
            fetchPatients(); // Refresh the list
        } catch (error) {
            console.error('Error disabling user:', error);
            toast.error('Error disabling user: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteUser = async (userId) => {
        const confirmDelete = window.confirm(`Delete this user's account? This cannot be undone.`);
        if (!confirmDelete) return;

        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            fetchPatients(); // Refresh the list
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Error deleting user: ' + (error.response?.data?.error || error.message));
        }
    };

    const handlePageChange = page => {
        setPagination(prev => ({ ...prev, page }));
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPagination(prev => ({ ...prev, perPage: newPerPage, page }));
    };

    const handleSort = (column, sortDirection) => {
        setSortConfig({ key: column.selector, direction: sortDirection });
        fetchPatients();
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        fetchPatients();
    };

    const filteredItems = patients?.filter(
        item => item && `${item.firstName || ''} ${item.lastName || ''}`.toLowerCase().includes(filterText.toLowerCase())
    ) || [];

    const handlePreview = (type) => {
        // Replace this logic with a real file URL lookup
        const dummyURL = type === 'MRI' ? '/sample.pdf' : '/xray.jpg';
        setPreviewFile({ url: dummyURL, type });
    };

    const handleClosePreview = () => setPreviewFile(null);

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(patients.map(patient => ({
            Name: `${patient.firstName} ${patient.lastName}`,
            Gender: patient.gender,
            Phone: patient.phone,
            Email: patient.email,
            'Created At': new Date(patient.createdAt).toLocaleDateString(),
            Status: patient.disabled ? "Disabled" : "Active"
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Patients");
        XLSX.writeFile(workbook, "patients.xlsx");
    };

    const fetchPatientDetails = async (patientId) => {
        if (!patientId) return;
        
        setLoadingDetails(true);
        try {
            const [vitalsResponse, documentsResponse, conditionsResponse] = await Promise.all([
                api.get(`/admin/users/${patientId}/vitals`),
                api.get(`/admin/users/${patientId}/documents`),
                api.get(`/admin/users/${patientId}/conditions`)
            ]);

            setPatientDetails({
                vitals: vitalsResponse.data || [],
                documents: documentsResponse.data || [],
                conditions: conditionsResponse.data || [],
                // Add other data as needed
            });
        } catch (error) {
            console.error('Error fetching patient details:', error);
            toast.error('Error fetching patient details: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoadingDetails(false);
        }
    };

    const handlePatientClick = (patient) => {
        if (!patient || typeof patient !== 'object') return;
        setSelectedPatient(patient);
        setActiveTab("vitals");
        fetchPatientDetails(patient.id);
    };

    const getVitalStats = (vitals = []) => {
        if (!vitals || !Array.isArray(vitals)) return {};

        const stats = {
            heartRate: { latest: null, average: null, min: null, max: null },
            bloodPressure: { latest: null, average: null, min: null, max: null },
            bloodSugar: { latest: null, average: null, min: null, max: null },
            temperature: { latest: null, average: null, min: null, max: null }
        };

        try {
            vitals.forEach(vital => {
                if (!vital || typeof vital !== 'object') return;

                const value = parseFloat(vital.value);
                if (isNaN(value)) return;

                const type = vital.type?.toLowerCase();
                if (!type || !stats[type]) return;

                // Update latest
                if (!stats[type].latest || new Date(vital.recordedAt) > new Date(stats[type].latest.recordedAt)) {
                    stats[type].latest = { value, recordedAt: vital.recordedAt };
                }

                // Update min/max
                if (stats[type].min === null || value < stats[type].min) {
                    stats[type].min = value;
                }
                if (stats[type].max === null || value > stats[type].max) {
                    stats[type].max = value;
                }
            });

            // Calculate averages
            Object.keys(stats).forEach(type => {
                const values = vitals
                    .filter(v => v?.type?.toLowerCase() === type)
                    .map(v => parseFloat(v.value))
                    .filter(v => !isNaN(v));

                if (values.length > 0) {
                    stats[type].average = values.reduce((a, b) => a + b, 0) / values.length;
                }
            });
        } catch (error) {
            console.error('Error calculating vital stats:', error);
        }

        return stats;
    };

    const renderVitalStats = (type) => {
        if (!selectedPatient?.vitals) return null;
        
        const stats = getVitalStats(selectedPatient.vitals);
        const typeStats = stats[type];
        if (!typeStats) return null;

        return (
            <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-2 capitalize">{type.replace(/([A-Z])/g, ' $1').trim()}</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-500">Latest</p>
                        <p className="text-xl font-bold">
                            {typeStats.latest ? `${typeStats.latest.value} ${getUnit(type)}` : 'N/A'}
                        </p>
                        {typeStats.latest && (
                            <p className="text-xs text-gray-400">
                                {new Date(typeStats.latest.recordedAt).toLocaleString()}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Average</p>
                        <p className="text-xl font-bold">
                            {typeStats.average ? `${typeStats.average.toFixed(1)} ${getUnit(type)}` : 'N/A'}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Min</p>
                        <p className="text-xl font-bold">
                            {typeStats.min !== null ? `${typeStats.min} ${getUnit(type)}` : 'N/A'}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Max</p>
                        <p className="text-xl font-bold">
                            {typeStats.max !== null ? `${typeStats.max} ${getUnit(type)}` : 'N/A'}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const renderPatientDetails = () => {
        if (!selectedPatient || typeof selectedPatient !== 'object') return null;

        return (
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-2xl font-bold">{selectedPatient.name || 'Unknown Patient'}</h2>
                        <p className="text-gray-500">{selectedPatient.email || 'No email provided'}</p>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handleDisableUser(selectedPatient.id)}
                            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                        >
                            {selectedPatient.disabled ? "Enable" : "Disable"}
                        </button>
                        <button
                            onClick={() => handleDeleteUser(selectedPatient.id)}
                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                            Delete
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                        <p className="text-sm text-gray-500">Gender</p>
                        <p className="font-medium">{selectedPatient.gender || 'Not specified'}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="font-medium">{selectedPatient.disabled ? "Disabled" : "Active"}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Last Login</p>
                        <p className="font-medium">
                            {selectedPatient.lastLogin 
                                ? new Date(selectedPatient.lastLogin).toLocaleString() 
                                : 'Never'}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Account Created</p>
                        <p className="font-medium">
                            {selectedPatient.createdAt 
                                ? new Date(selectedPatient.createdAt).toLocaleString() 
                                : 'Unknown'}
                        </p>
                    </div>
                </div>

                <div className="border-t pt-6">
                    <div className="flex space-x-4 mb-4">
                        <button
                            onClick={() => setActiveTab("vitals")}
                            className={`px-4 py-2 rounded ${
                                activeTab === "vitals"
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-200 text-gray-700"
                            }`}
                        >
                            Vitals
                        </button>
                        <button
                            onClick={() => setActiveTab("documents")}
                            className={`px-4 py-2 rounded ${
                                activeTab === "documents"
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-200 text-gray-700"
                            }`}
                        >
                            Documents
                        </button>
                    </div>

                    {activeTab === "vitals" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {renderVitalStats("heartRate")}
                            {renderVitalStats("bloodPressure")}
                            {renderVitalStats("bloodSugar")}
                            {renderVitalStats("temperature")}
                        </div>
                    )}

                    {activeTab === "documents" && (
                        <div className="space-y-4">
                            {selectedPatient.documents?.length > 0 ? (
                                selectedPatient.documents.map((doc) => (
                                    <div
                                        key={doc.id}
                                        className="flex items-center justify-between p-4 bg-gray-50 rounded"
                                    >
                                        <div>
                                            <p className="font-medium">{doc.fileName || 'Unnamed Document'}</p>
                                            <p className="text-sm text-gray-500">
                                                Uploaded: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : 'Unknown date'}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setPreviewFile(doc)}
                                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            Preview
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-center py-4">No documents found</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderVitalsTab = () => {
        if (loadingDetails) {
            return <div className="flex justify-center items-center p-4">Loading vitals...</div>;
        }

        if (!patientDetails?.vitals?.length) {
            return <p className="text-gray-500 text-center py-4">No vitals data available</p>;
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderVitalStats("heartRate")}
                {renderVitalStats("bloodPressure")}
                {renderVitalStats("bloodSugar")}
                {renderVitalStats("temperature")}
            </div>
        );
    };

    const renderDocumentsTab = () => {
        if (loadingDetails) {
            return <div className="flex justify-center items-center p-4">Loading documents...</div>;
        }

        if (!patientDetails?.documents?.length) {
            return <p className="text-gray-500 text-center py-4">No documents found</p>;
        }

        return (
            <div className="space-y-4">
                {patientDetails.documents.map((doc) => (
                    <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded"
                    >
                        <div>
                            <p className="font-medium">{doc.fileName || 'Unnamed Document'}</p>
                            <p className="text-sm text-gray-500">
                                Uploaded: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : 'Unknown date'}
                            </p>
                        </div>
                        <button
                            onClick={() => setPreviewFile(doc)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Preview
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    const renderConditionsTab = () => {
        if (loadingDetails) {
            return <div className="flex justify-center items-center p-4">Loading conditions...</div>;
        }

        if (!patientDetails?.conditions?.length) {
            return <p className="text-gray-500 text-center py-4">No conditions found</p>;
        }

        return (
            <div className="space-y-4">
                {patientDetails.conditions.map((condition) => (
                    <div key={condition.id} className="p-4 bg-gray-50 rounded">
                        <h4 className="font-medium">{condition.name}</h4>
                        <p className="text-sm text-gray-600">{condition.description}</p>
                        <p className="text-xs text-gray-500 mt-2">
                            Diagnosed: {condition.diagnosedAt ? new Date(condition.diagnosedAt).toLocaleDateString() : 'Unknown'}
                        </p>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <Layout>
            <div className="p-6 space-y-4">
                <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">Patients</h1>
                    <button
                        onClick={exportToExcel}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                        <FaDownload /> Export to Excel
                    </button>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Error!</strong>
                        <span className="block sm:inline"> {error}</span>
                        <button 
                            className="absolute top-0 bottom-0 right-0 px-4 py-3"
                            onClick={() => setError(null)}
                        >
                            <span className="sr-only">Dismiss</span>
                            ✕
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                    type="text"
                    placeholder="Search patients..."
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                        className="border p-2 rounded"
                    />
                    <select
                        value={filters.gender}
                        onChange={e => handleFilterChange('gender', e.target.value)}
                        className="border p-2 rounded"
                    >
                        <option value="">All Genders</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </select>
                    <select
                        value={filters.status}
                        onChange={e => handleFilterChange('status', e.target.value)}
                        className="border p-2 rounded"
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="disabled">Disabled</option>
                    </select>
                </div>

                <DataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationServer
                    paginationTotalRows={pagination.total}
                    paginationPerPage={pagination.perPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onSort={handleSort}
                    sortServer
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={row => handlePatientClick(row)}
                    progressPending={loading}
                    progressComponent={
                        <div className="flex justify-center items-center p-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        </div>
                    }
                    noDataComponent={
                        <div className="text-center py-4">
                            {error ? (
                                <p className="text-red-500">Error loading data. Please try again.</p>
                            ) : (
                                <p>No patients found.</p>
                            )}
                        </div>
                    }
                />
            </div>

            {selectedPatient && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-lg">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">
                                {selectedPatient.firstName} {selectedPatient.lastName}
                            </h2>
                            <button
                                onClick={() => {
                                    setSelectedPatient(null);
                                    setPatientDetails(null);
                                }}
                                className="text-gray-500 hover:text-red-600 text-lg font-bold"
                            >✕</button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div><strong>Gender:</strong> {selectedPatient.gender || 'Not specified'}</div>
                            <div><strong>DOB:</strong> {selectedPatient.dob || 'Not specified'}</div>
                            <div><strong>Age:</strong> {selectedPatient.age || 'Not specified'}</div>
                            <div><strong>Phone:</strong> {selectedPatient.phone || 'Not specified'}</div>
                            <div><strong>Parish:</strong> {selectedPatient.parish || 'Not specified'}</div>
                            <div><strong>Last Vitals:</strong> {selectedPatient.lastVitalsDate ? new Date(selectedPatient.lastVitalsDate).toLocaleDateString() : 'Never'}</div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-4 border-b pb-2">
                            <button 
                                onClick={() => setActiveTab("vitals")} 
                                className={`px-3 py-1 rounded ${activeTab === "vitals" ? "bg-blue-100" : "bg-gray-100"}`}
                            >
                                Vitals
                            </button>
                            <button 
                                onClick={() => setActiveTab("conditions")} 
                                className={`px-3 py-1 rounded ${activeTab === "conditions" ? "bg-blue-100" : "bg-gray-100"}`}
                            >
                                Conditions
                            </button>
                            <button 
                                onClick={() => setActiveTab("documents")} 
                                className={`px-3 py-1 rounded ${activeTab === "documents" ? "bg-blue-100" : "bg-gray-100"}`}
                            >
                                Documents
                            </button>
                            {/* Add more tabs as needed */}
                        </div>

                        {activeTab === "vitals" && renderVitalsTab()}
                        {activeTab === "conditions" && renderConditionsTab()}
                        {activeTab === "documents" && renderDocumentsTab()}

                        <div className="mt-6 flex justify-between">
                            <button
                                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                                onClick={() => {
                                    import('jspdf').then(jsPDFModule => {
                                        const doc = new jsPDFModule.jsPDF();
                                        doc.text("Patient Summary Export", 10, 10);
                                        doc.text("(Generate more content dynamically here...)", 10, 20);
                                        doc.save("patient_summary.pdf");
                                    });
                                }}
                            >
                                <FaUserEdit />Print / Export Profile
                            </button>
                            <button className="flex items-center gap-2 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600">
                                <FaBell /> Send Alert
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {previewFile && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
                        <button onClick={handleClosePreview} className="absolute top-2 right-2 text-gray-600 text-lg">✕</button>
                        <h2 className="text-lg font-semibold mb-2">Preview: {previewFile.type}</h2>
                        {previewFile.url.endsWith('.pdf') ? (
                            <iframe src={previewFile.url} className="w-full h-[75vh]" title="PDF Preview" />
                        ) : (
                            <img src={previewFile.url} alt="Document Preview" className="w-full max-h-[75vh] object-contain" />
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
}
